import { c as _c } from "react/compiler-runtime";
import { getLogger } from '@onefootprint/logger';
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Button, SplitButton, Tooltip } from '@onefootprint/ui';
import dynamic from 'next/dynamic';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener } from 'usehooks-ts';
import ManualReview from '@/components/manual-review';
import PermissionGate from '@/components/permission-gate';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useAutoDecryptControls from '@/entity/hooks/use-auto-decrypt-controls';
import useCurrentEntity from '@/entity/hooks/use-current-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useDocuments from '@/entity/hooks/use-documents';
import hasSomeDiDecryptable from '@/utils/has-some-di-decryptable';
import BusinessActions from './components/business-actions';
import PersonActions from './components/person-actions';
import getAutoDecryptFields from './utils/get-auto-decrypt-fields';
const ReasonDialog = dynamic(() => import('./components/reason-dialog'), {
  ssr: false
});
const ManageAutoDecryptDialog = dynamic(() => import('./components/manage-auto-decrypt-dialog'), {
  ssr: false
});
const {
  logError
} = getLogger({
  location: 'vault-actions'
});
enum Action {
  EditTags = 'edit-tags',
  DecryptAll = 'decrypt-all',
  DecryptSome = 'decrypt-some',
  AutoDecryptAll = 'auto-decrypt-all',
  AutoDecryptManage = 'auto-decrypt-manage',
  AutoDecryptAdditional = 'auto-decrypt-additional',
}
type ShortcutConfig = {
  keys: string[];
};
const SHORTCUTS: Record<Exclude<Action, Action.AutoDecryptAll>, ShortcutConfig> = {
  [Action.EditTags]: {
    keys: ['Meta']
  },
  [Action.DecryptAll]: {
    keys: ['Control', 'a']
  },
  [Action.DecryptSome]: {
    keys: ['Control', 's']
  },
  [Action.AutoDecryptManage]: {
    keys: ['Control', 'a', 'm']
  },
  [Action.AutoDecryptAdditional]: {
    keys: ['Control', 'a', 'f']
  }
};
export type VaultActionsProps = WithEntityProps & {
  // Drops the Decrypt SplitButton (and its dialogs) when false. Used by hosts
  // that mount these actions outside the /users/[id] route (e.g. the
  // investigation page), where decryption isn't offered.
  showDecrypt?: boolean;
};
const VaultActions = t0 => {
  const $ = _c(18);
  const {
    entity,
    showDecrypt: t1
  } = t0;
  const showDecrypt = t1 === undefined ? true : t1;
  const [isAutoDecryptDialogOpen, setIsAutoDecryptDialogOpen] = useState(false);
  const decryptControls = useDecryptControls();
  const autoDecryptControls = useAutoDecryptControls(entity.kind);
  let t2;
  if ($[0] !== decryptControls) {
    t2 = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        decryptControls.cancel();
      }
    };
    $[0] = decryptControls;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEventListener("keydown", t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsAutoDecryptDialogOpen(true);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleOpenAutoDecryptDialog = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsAutoDecryptDialogOpen(false);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleCloseAutoDecryptDialog = t4;
  let t5;
  if ($[4] !== autoDecryptControls || $[5] !== decryptControls.step || $[6] !== entity || $[7] !== showDecrypt) {
    t5 = decryptControls.step === "idle" && <IdleActions entity={entity} isAutoDecryptEnabled={autoDecryptControls.enabled} onClickAutoDecrypt={handleOpenAutoDecryptDialog} showDecrypt={showDecrypt} />;
    $[4] = autoDecryptControls;
    $[5] = decryptControls.step;
    $[6] = entity;
    $[7] = showDecrypt;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== decryptControls.cancel || $[10] !== decryptControls.step || $[11] !== entity || $[12] !== isAutoDecryptDialogOpen || $[13] !== showDecrypt) {
    t6 = showDecrypt && <Suspense fallback={null}>{(decryptControls.step === "selecting-fields" || decryptControls.step === "selecting-reason") && <SelectingFieldsActions />}<ReasonDialog entity={entity} onClose={decryptControls.cancel} open={decryptControls.step === "selecting-reason"} /><ManageAutoDecryptDialog onClose={handleCloseAutoDecryptDialog} open={isAutoDecryptDialogOpen} /></Suspense>;
    $[9] = decryptControls.cancel;
    $[10] = decryptControls.step;
    $[11] = entity;
    $[12] = isAutoDecryptDialogOpen;
    $[13] = showDecrypt;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
const IdleActions = t0 => {
  const $ = _c(39);
  const {
    entity,
    isAutoDecryptEnabled,
    onClickAutoDecrypt,
    showDecrypt
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  const decryptControls = useDecryptControls();
  const autoDecryptControls = useAutoDecryptControls(entity.kind);
  const decryptForm = useDecryptForm();
  let t1;
  if ($[0] !== entity.data || $[1] !== entity.id) {
    t1 = {
      entityAttributes: entity.data,
      entityId: entity.id
    };
    $[0] = entity.data;
    $[1] = entity.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const entityVaultQuery = useEntityVault(t1);
  const {
    data: entityData
  } = useCurrentEntity();
  const {
    data: documents
  } = useDocuments(entity.id);
  let t2;
  if ($[3] !== documents || $[4] !== entity) {
    t2 = hasSomeDiDecryptable(entity, documents);
    $[3] = documents;
    $[4] = entity;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const canDecrypt = t2;
  let t3;
  if ($[6] !== entityVaultQuery.data?.vault) {
    t3 = Object.keys(entityVaultQuery.data?.vault || {});
    $[6] = entityVaultQuery.data?.vault;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isVaultEmpty = t3.length === 0;
  const shouldRenderManualReview = entityData && !!entityData.workflows.length;
  let t4;
  if ($[8] !== autoDecryptControls || $[9] !== decryptControls || $[10] !== decryptForm || $[11] !== entity || $[12] !== entityVaultQuery) {
    t4 = async () => {
      await decryptControls.selectAllFields();
      const fields = decryptForm.getFields().filter(field => !autoDecryptControls.isSensitiveField(field));
      const autoDecryptFields = getAutoDecryptFields({
        selectedFields: fields,
        excludedPrefixes: autoDecryptControls.sensitiveFieldPrefixes,
        vaultData: entityVaultQuery.data?.vault || {}
      });
      autoDecryptControls.enableAutoDecrypt();
      autoDecryptControls.setReason("Auto-decrypt all");
      autoDecryptControls.setFields(autoDecryptFields);
      const response = await decryptControls.decrypt({
        entity,
        fields,
        reason: "Auto-decrypt all"
      });
      if (response) {
        entityVaultQuery.update({
          vault: response,
          transforms: {},
          dataKinds: {}
        });
      } else {
        logError("Vault decryption error", undefined, {
          entityId: entity.id,
          fields: fields.join(","),
          detail: "No response in Vault actions > Auto-decrypt all"
        });
      }
    };
    $[8] = autoDecryptControls;
    $[9] = decryptControls;
    $[10] = decryptForm;
    $[11] = entity;
    $[12] = entityVaultQuery;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const onAutoDecryptAll = t4;
  let t5;
  if ($[14] !== decryptControls.startToDecryptAll || $[15] !== decryptControls.startToSelectFields || $[16] !== entity.kind || $[17] !== isAutoDecryptEnabled || $[18] !== onAutoDecryptAll || $[19] !== onClickAutoDecrypt || $[20] !== t) {
    t5 = isAutoDecryptEnabled ? [{
      label: t(_temp),
      value: Action.AutoDecryptManage,
      onSelect: onClickAutoDecrypt,
      shortcut: SHORTCUTS[Action.AutoDecryptManage].keys
    }, {
      label: t(_temp2, {
        kind: entity.kind
      }),
      value: Action.AutoDecryptAdditional,
      onSelect: decryptControls.startToSelectFields,
      shortcut: SHORTCUTS[Action.AutoDecryptAdditional].keys
    }] : [{
      label: t(_temp3),
      value: Action.DecryptSome,
      onSelect: decryptControls.startToSelectFields,
      shortcut: SHORTCUTS[Action.DecryptSome].keys
    }, {
      label: t(_temp4),
      value: Action.DecryptAll,
      onSelect: decryptControls.startToDecryptAll,
      shortcut: SHORTCUTS[Action.DecryptAll].keys
    }, {
      label: t(_temp5),
      value: Action.AutoDecryptAll,
      onSelect: onAutoDecryptAll,
      shortcut: undefined
    }];
    $[14] = decryptControls.startToDecryptAll;
    $[15] = decryptControls.startToSelectFields;
    $[16] = entity.kind;
    $[17] = isAutoDecryptEnabled;
    $[18] = onAutoDecryptAll;
    $[19] = onClickAutoDecrypt;
    $[20] = t;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const decryptOptions = t5;
  let t6;
  if ($[22] !== canDecrypt || $[23] !== decryptOptions || $[24] !== isAutoDecryptEnabled || $[25] !== isVaultEmpty || $[26] !== showDecrypt || $[27] !== t) {
    t6 = showDecrypt && <Tooltip disabled={canDecrypt} content={isVaultEmpty ? t(_temp6) : t(_temp7)}><SplitButton key={`decrypt-options-${isAutoDecryptEnabled}`} disabled={!canDecrypt} variant="secondary" size="compact" options={decryptOptions} /></Tooltip>;
    $[22] = canDecrypt;
    $[23] = decryptOptions;
    $[24] = isAutoDecryptEnabled;
    $[25] = isVaultEmpty;
    $[26] = showDecrypt;
    $[27] = t;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== entityData || $[30] !== shouldRenderManualReview || $[31] !== t) {
    t7 = shouldRenderManualReview && <PermissionGate scopeKind="manual_review" fallbackText={t(_temp8)}><ManualReview status={entityData.status} kind={entityData.kind} /></PermissionGate>;
    $[29] = entityData;
    $[30] = shouldRenderManualReview;
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== entity.kind) {
    t8 = entity.kind === "person" ? <PersonActions /> : <BusinessActions />;
    $[33] = entity.kind;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <div className="flex flex-wrap gap-2 items-center md:flex-nowrap">{t6}{t7}{t8}</div>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
};
const SelectingFieldsActions = () => {
  const $ = _c(18);
  const {
    t
  } = useTranslation("entity-details");
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t0;
  if ($[0] !== decryptControls) {
    t0 = () => {
      decryptControls.cancel();
    };
    $[0] = decryptControls;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleCancel = t0;
  let t1;
  if ($[2] !== decryptControls || $[3] !== decryptForm) {
    t1 = () => {
      const fields = decryptForm.getValues("fields");
      const hasNewFieldsToDecrypt = fields.some(field => !decryptControls.decryptedFields.includes(field));
      if (hasNewFieldsToDecrypt) {
        decryptControls.selectReason();
      }
    };
    $[2] = decryptControls;
    $[3] = decryptForm;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleNext = t1;
  let t2;
  if ($[5] !== t) {
    t2 = t(_temp9);
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== handleCancel || $[8] !== t2) {
    t3 = <Button variant="secondary" size="compact" onClick={handleCancel}>{t2}</Button>;
    $[7] = handleCancel;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t) {
    t4 = t(_temp10);
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== handleNext || $[13] !== t4) {
    t5 = <Button size="compact" onClick={handleNext}>{t4}</Button>;
    $[12] = handleNext;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t5) {
    t6 = <div className="flex gap-2">{t3}{t5}</div>;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export default VaultActions;
function _temp($) {
  return $.decrypt.autoDecrypt.manage;
}
function _temp2($_0) {
  return $_0.decrypt.autoDecrypt.additional;
}
function _temp3($_1) {
  return $_1.decrypt.start;
}
function _temp4($_2) {
  return $_2.decrypt.startAll;
}
function _temp5($_3) {
  return $_3.decrypt.startAutoDecryptAll;
}
function _temp6($_4) {
  return $_4.decrypt.vaultEmptyNotAllowed;
}
function _temp7($_5) {
  return $_5.decrypt.notAllowed;
}
function _temp8($_6) {
  return $_6.manualReviewNotAllowed;
}
function _temp9($) {
  return $.cancel;
}
function _temp10($_0) {
  return $_0.next;
}